/* eslint-disable react/jsx-handler-names */
import { twMerge } from 'cn';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';
import { shallow } from 'zustand/shallow';
import { Panel } from './store/types.js';
import { useStore } from './store/hooks.js';
import { useWiring } from './wiring.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { Sel, STag } from '#~/stag/index.js';

/** Settings `<Footer>` component. */
export const Footer = (props: _Footer.Props): ReactElement => {
  const flags = useStore((µ) => µ.flags, shallow);
  const wiring = useWiring();
  const verification = useStore((state) => state.verification);
  const verifying = verification?.status === 'pending';
  const activeTab = useStore((state) => state.irisConnectionActiveTab);
  const panel = useStore((state) => state.panel);
  const authenticationForm = useStore((state) => state.authenticationForm);
  // The last connection sub-tab remains in the store when another main pane opens.
  const isAuthentication = panel === Panel.IrisConnection && activeTab === 'authentication';
  // Credentials its last Submit verified need no second verification, so the footer finishes instead.
  const submitsCredentials = isAuthentication && !authenticationForm?.verified;
  const unverified = verification && verification.status !== 'verified';

  const { isBusy } = flags;

  const left = flags.canUndo
    ? {
        handler: wiring.handleUndo,
        label: isBusy ? 'Updating...' : 'Undo Changes'
      }
    : {
        handler: wiring.handleCancel,
        label: 'Cancel'
      };

  const retryVerification =
    !isBusy && unverified && verification.step === 'driver' && activeTab === 'driver';
  // A saved driver whose connection is still unverified (video may already be
  // playing) must not trap the user behind Retry / Undo (QA finding 9): Close
  // becomes the primary, safe way out and Retry stays one Tab away as a
  // secondary button. With unsaved edits Retry keeps the primary slot, since
  // it is also what commits those edits.
  const offerClose = retryVerification && !flags.isDirty;
  const right = verifying
    ? { disabled: true, label: 'Verifying...' }
    : submitsCredentials
      ? { disabled: !authenticationForm?.canSubmit, label: 'Save Changes' }
      : offerClose
        ? { handler: wiring.handleClose, label: 'Close' }
        : retryVerification
          ? { handler: wiring.handleCommit, label: 'Retry verification' }
          : flags.canUndo
            ? {
                handler: wiring.handleClose,
                label: verifying ? 'Verifying...' : unverified ? 'Close' : 'Done'
              }
            : flags.isDirty
              ? {
                  handler: wiring.handleCommit,
                  label: verifying ? 'Verifying...' : isBusy ? 'Saving...' : 'Save Changes'
                }
              : isAuthentication
                ? { handler: wiring.handleClose, label: 'Done' }
                : { disabled: true, label: 'Save Changes' };

  return (
    // When Close is offered there are three actions: Undo stays left, Retry +
    // Close group right (same layout as the audio settings footer).
    <_Footer.Root
      {...props}
      className={offerClose ? twMerge('justify-start gap-8', props.className) : props.className}
    >
      <_Footer.Button
        {...STag(Sel.VideoSettings, flags.canUndo ? 'undo' : 'cancel')}
        disabled={flags.isBusy}
        onClick={left.handler}
        variant='secondary'
      >
        {left.label}
      </_Footer.Button>

      {/* Design-system Button (native <button>) keeps Retry Tab-reachable and
          Enter/Space-activatable. Per `keyboard-reachable-interactive-elements`
          (ai.knowledge/global). */}
      {offerClose && (
        <_Footer.Button
          {...STag(Sel.VideoSettings, 'retry-verification')}
          className='ml-auto'
          onClick={wiring.handleCommit}
          variant='secondary'
        >
          Retry verification
        </_Footer.Button>
      )}

      <_Footer.Next
        disabled={flags.isBusy || (right.disabled ?? false)}
        label={right.label}
        type={submitsCredentials ? 'submit' : 'button'}
        form={submitsCredentials ? authenticationForm?.id : undefined}
        onClick={submitsCredentials ? undefined : (right.handler ?? noop)}
      />
    </_Footer.Root>
  );
};
